import React from 'react';
import 'url-search-params-polyfill';
import styled from 'styled-components';
import Head from 'next/head';
import PageContainer from './PageContainer';
import MainLayout from '../../../templates/MainLayout';
import MenuWrapper from '../MenuWrapper/MenuWrapper';
import Footer from '../Footer/Footer';
import GlobalBanner from '../Banner/GlobalBanner';
import Modal from '../Modal/Modal';
import {
  resolveInterstitialURI,
  getInitAdSlotsScript,
} from '../../../../lib/utils';
import OnboardingModalContainer from '../OnboardingModal/OnboardingModalContainer';
import SessionExtensionContainer from '../SessionExtension/SessionExtensionContainer';
import { AnalyticsPageContext } from './AnalyticsPageContext';

const PageBodyDiv = styled.div`
  min-height: calc(100vh - 504px);
`;

const hasOnboardingData = (onboardingModalData) => {
  if (!onboardingModalData || typeof onboardingModalData !== 'object' || Array.isArray(onboardingModalData)) {
    return false;
  }

  const {
    headerText,
    subHeaderText,
    buttonText,
    slots,
    bottomBannerHeaderText,
    bottomBannerSubHeaderText,
  } = onboardingModalData;

  return Boolean(
    headerText ||
    subHeaderText ||
    buttonText ||
    bottomBannerHeaderText ||
    bottomBannerSubHeaderText ||
    (Array.isArray(slots) && slots.length > 0)
  );
};

/**
 * @typedef {Object} UrlData
 * @property {string} [hostname]
 * @property {string} [pathname]
 * @property {string} [origin]
 */

/**
 * @typedef {Object} RequestData
 * @property {string} urlLocale - Current locale (e.g., "en")
 * @property {UrlData} urlData - Information about the current URL
 * @property {boolean} [urlShowSignIn] - Whether to show sign-in modal
 * @property {Object} urlQueryParams - Parsed query parameters
 */

/**
 * @typedef {Object} UserSessionData
 * @property {Object} userData - Authenticated user data
 * @property {UserShopsData} [userShopsData]
 * @property {UserOffersData} [userOffersData]
 */

/**
 * @typedef {Object} Props
 * @property {string} pageName - Name/ID of the current page
 * @property {string} [metaDescription] - Meta description for SEO
 * @property {string} [pageTitle=""] - Page title for SEO
 * @property {RequestData} requestData - Data derived from URL context
 * @property {UserSessionData} userSessionData - Data related to the signed-in user
 * @property {Object} globalPageData - Global data for the page
 * @property {Array} [pageAds=[]] - List of ads to initialise on the Page
 * @property {string} [analyticsPageName] - Optional analytics name passed to page components
 */

/**
 * @extends {React.Component<Props>}
 */
class Page extends PageContainer {
  componentDidMount() {
    super.componentDidMount();
    const {
      requestData,
      pageAds = [],
      userSessionData,
      requestData: {
        urlLocale: locale,
        urlShowSignIn,
      }
    } = this.props;

    const {
      affiliateUrl,
      shopSlug,
      offerSlug,
      adSlug,
    } = requestData.urlQueryParams;

    // Should be moved server-side
    if (urlShowSignIn && !this.pageStateModifiers.isUserLoggedIn()) {
      this.pageStateModifiers.makeAndOpenSignInModal();
      return;
    } else if (urlShowSignIn && this.pageStateModifiers.isUserLoggedIn()) {
      const redirectAffiliateUrl = this.props.affiliateUrl || affiliateUrl;
      const shopSlugToUse = this.props.shopSlug || shopSlug;
      const offerSlugToUse = this.props.offerSlug || offerSlug;
      const adSlugToUse = this.props.adSlug || adSlug;

      if (redirectAffiliateUrl) {
        const resolvedURIObj = resolveInterstitialURI({
          shopSlug: shopSlugToUse,
          offerSlug: offerSlugToUse,
          adSlug: adSlugToUse,
          locale,
        });
        window.location = resolvedURIObj.externalRedirectUrl;
      }
    }

    if (pageAds && pageAds.length > 0 && window.adsEnabled) {
      const intitAdSlotsScript = getInitAdSlotsScript(pageAds, userSessionData?.userData?.ppid);
      if (intitAdSlotsScript) {
        const script = window.document.createElement('script');
        script.textContent = intitAdSlotsScript;
        window.document.head.append(script);
      }
    }
  }

  render() {
    const {
      pageName = '',
      pageTitle = '',
      metaDescription,
      globalPageData,
      requestData,
      userSessionData,
      analyticsPageName,
    } = this.props;

    const {
      urlLocale,
      urlData,
      urlQueryParams,
    } = requestData;

    const {
      userData,
    } = userSessionData;

    const {
      searchNavBarData,
      catNavBarData,
      globalNavData,
      footerData,
      bannerData,
      notificationData,
      onboardingModal,
      defaultMetaDescription,
      pageTitleBase,
      globalPageConfig,
    } = globalPageData;

    const metaDescriptionToUse = metaDescription ?? defaultMetaDescription;

    const pageTitleToUse = `${pageTitle} ${pageTitleBase}`;
    const canonicalLink = urlData?.origin && urlData?.pathname
      ? `${urlData.origin}${urlData.pathname}`
      : '';

    const childrenWithProps = React.Children.map(this.props.children,
      child => React.cloneElement(child, {
        userShopsData: this.state.userShopsData,
        pageStateModifiers: this.pageStateModifiers,
        userData: this.state.userData,
        globalPageConfig,
      }),
    );

    this.globalPageConfig = globalPageConfig;
    this.onboardingModal = hasOnboardingData(onboardingModal) ? onboardingModal : null;

    const analyticsPageContextValue = {
      analyticsPageName,
      userData: this.state.userData,
      locale: urlLocale,
    };

    const pageContent = (
      <MainLayout>
        {/* Head */}
        <Head>
          <title>{pageTitleToUse}</title>
          <meta name="description" content={metaDescriptionToUse} />
          {canonicalLink && <link rel="canonical" href={canonicalLink} />}
        </Head>

        <MenuWrapper
          pageName={pageName}
          searchNavBarData={searchNavBarData}
          catNavBarData={catNavBarData}
          globalNavData={globalNavData}
          userShopsData={this.state.userShopsData}
          locale={urlLocale}
          isUserLoggedIn={this.pageStateModifiers.isUserLoggedIn}
          pageStateModifiers={this.pageStateModifiers}
          selectedDropDown={this.state.selectedDropDown}
          localeUrls={urlQueryParams.localeUrls || {}}
          subCategoryLocaleSlugMap={urlQueryParams.subCategoryLocaleSlugMap || {}}
          globalPageConfig={globalPageConfig}
          notificationData={notificationData}
          userData={userData}
        >
          <main id="main-content">
            <PageBodyDiv>
              {childrenWithProps}
            </PageBodyDiv>
          </main>

          {/* Footer */}
          <Footer
            locale={urlLocale}
            {...footerData} />

          {/* Global Banner */}
          <GlobalBanner
            {...bannerData}
            locale={urlLocale}
            accessibilityLabel={globalPageConfig?.globalBannerLabel}
            closeIconLabel={globalPageConfig?.closeIconLabel}
          />
        </MenuWrapper>

        {/* Only instance of modal for the page */}
        <Modal
          ref={(element) => { this.modal = element; }}
          urlQueryParams={urlQueryParams}
          {...globalPageConfig} />

        <OnboardingModalContainer
          pageStateModifiers={this.pageStateModifiers}
          requestData={requestData}
        />

        <SessionExtensionContainer
          pageStateModifiers={this.pageStateModifiers}
          locale={urlLocale}
          {...globalPageConfig}
        />
      </MainLayout>
    );

    return (
      <AnalyticsPageContext.Provider value={analyticsPageContextValue}>
        {pageContent}
      </AnalyticsPageContext.Provider>
    );
  }
}

export default Page;
